import { useEffect, useState, useCallback, useRef } from "react";
import {
  Download, Upload, Loader2, CheckCircle2, AlertTriangle, FileWarning, Undo2, Database,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loading, EmptyState } from "@/components/StateViews";
import ColumnMappingModal from "@/components/ColumnMappingModal";
import CrmImportWizard from "@/components/CrmImportWizard";
import MappingTemplatesPanel from "@/components/MappingTemplatesPanel";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtDateTime } from "@/lib/format";

function Stat({ label, value, color = "text-navy" }) {
  return <div className="rounded-xl bg-mist px-3 py-2 text-center"><p className={`font-sora text-lg font-bold ${color}`}>{value}</p><p className="text-[11px] text-ink/50">{label}</p></div>;
}

function UploadCard({ title, description, icon: Icon, iconColor, kind, uploading, onPick, testid }) {
  const ref = useRef();
  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid={`upload-card-${kind}`}>
      <div className="mb-3 flex items-center gap-2"><Icon className={`h-5 w-5 ${iconColor}`} /><h2 className="font-sora font-semibold text-navy">{title}</h2></div>
      <p className="mb-3 text-xs text-ink/60">{description}</p>
      <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-sky bg-mist/50 py-7 transition-colors hover:border-loop hover:bg-sky/20">
        {uploading ? <Loader2 className="h-6 w-6 animate-spin text-loop" /> : <Upload className="h-6 w-6 text-loop" />}
        <span className="mt-2 text-sm font-medium text-navy">{uploading ? "Reading file…" : "Click to upload .csv or .xlsx"}</span>
        <input ref={ref} data-testid={testid} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { onPick(e.target.files[0], kind); if (ref.current) ref.current.value = ""; }} />
      </label>
    </div>
  );
}

export default function ImportCenter() {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [imports, setImports] = useState([]);
  const [issues, setIssues] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rollbackTarget, setRollbackTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [imp, iss, tpl] = await Promise.all([
        api.get("/imports"), api.get("/import-issues"), api.get("/import-templates"),
      ]);
      setImports(imp.data.data);
      setIssues(iss.data.data);
      setTemplates(tpl.data.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Let the browser/axios set the multipart boundary automatically — do NOT
      // hardcode Content-Type or the boundary is dropped and parsing fails.
      const url = `/imports/csv/preview${kind ? `?import_kind=${kind}` : ""}`;
      const { data } = await api.post(url, fd);
      setPreview(data.data);
      // CRM/Bitrix uploads use the multi-step migration wizard; Meta uses the simple mapper.
      if (kind === "crm" || (data.data.columns && data.data.detected_import_type !== "meta")) {
        setCrmOpen(true);
      } else {
        setMapOpen(true);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const doRollback = async (imp) => {
    try {
      const { data } = await api.post(`/imports/${imp.id}/rollback`);
      toast.success(`Rolled back · ${data.data.deleted} lead(s) moved to recycle bin`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadRows = async (imp) => {
    try {
      const res = await api.get(`/imports/${imp.id}/issues.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `import_${imp.id}_unprocessed_rows.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <Loading label="Loading import center..." />;

  return (
    <div className="space-y-6" data-testid="import-center-page">
      <div>
        <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Download className="h-7 w-7 text-loop" />Import Center</h1>
        <p className="text-sm text-ink/60">Upload a Meta Lead Ads CSV as a backup, or migrate leads in bulk from another CRM (Bitrix / Excel / CSV). You will confirm the column mapping and how duplicates are handled before importing.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <UploadCard
          kind="meta" testid="csv-upload-input"
          title="Manual Meta CSV Upload" icon={Upload} iconColor="text-loop"
          description="Backup / recovery for the Meta → n8n ingestion. Upload a Meta Lead Ads CSV export; duplicates are skipped by default so live leads are never overwritten."
          uploading={uploading} onPick={upload}
        />
        <UploadCard
          kind="crm" testid="crm-upload-input"
          title="Bulk CRM Migration Import" icon={Database} iconColor="text-emerald-600"
          description="One-time migration from another CRM (Bitrix, HubSpot, etc.) via CSV or Excel. Map the columns once and choose how existing leads are handled."
          uploading={uploading} onPick={upload}
        />
      </div>

      {/* Mapping templates (reusable column maps for recurring CSV layouts) */}
      <MappingTemplatesPanel templates={templates} onChanged={load} />

      {/* Recent imports */}
      <div className="rounded-2xl border border-sky/70 bg-white p-5">
        <h2 className="mb-4 font-sora font-semibold text-navy">Import History</h2>
        {imports.length === 0 ? <EmptyState title="No imports yet" subtitle="Upload a Meta CSV or migrate from another CRM to get started." /> : (
          <div className="space-y-3">
            {imports.map((imp) => (
              <div key={imp.id} data-testid={`import-row-${imp.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sky/60 p-3">
                <div className="flex items-center gap-3">
                  {imp.status === "completed" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : imp.status === "failed" ? <AlertTriangle className="h-5 w-5 text-red-500" /> : <FileWarning className="h-5 w-5 text-amber-500" />}
                  <div>
                    <p className="flex items-center gap-2 text-sm font-medium text-navy">{imp.import_type} {imp.file_name && `· ${imp.file_name}`}
                      {imp.rolled_back && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Rolled back</span>}
                    </p>
                    <p className="text-xs text-ink/50">{fmtDateTime(imp.imported_at)} · by {imp.imported_by_name || "—"}{imp.duplicate_handling ? ` · dupes: ${imp.duplicate_handling}` : ""}</p>
                    {imp.stage_breakdown && Object.keys(imp.stage_breakdown).length > 0 && (
                      <p className="mt-1 flex flex-wrap gap-1.5" data-testid={`import-stage-summary-${imp.id}`}>
                        {Object.entries(imp.stage_breakdown).map(([s, n]) => (
                          <span key={s} className="rounded-full bg-sky/50 px-2 py-0.5 text-[10px] font-semibold text-navy">{s}: {n}</span>
                        ))}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="grid grid-cols-4 gap-2">
                    <Stat label="New" value={imp.new_leads} color="text-emerald-600" />
                    <Stat label="Updated" value={imp.updated_leads} color="text-loop" />
                    <Stat label="Dupes" value={imp.duplicates_skipped} />
                    <Stat label="Issues" value={imp.issues_count} color={imp.issues_count ? "text-red-500" : "text-navy"} />
                  </div>
                  {(imp.issues_count > 0 || imp.duplicates_skipped > 0) && (
                    <button onClick={() => downloadRows(imp)} data-testid={`import-download-rows-${imp.id}`} title="Download the rows that failed or were skipped as duplicates" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-semibold text-loop hover:bg-sky/30"><Download className="h-3.5 w-3.5" />Download rows</button>
                  )}
                  {imp.new_leads > 0 && !imp.rolled_back && (
                    <button onClick={() => setRollbackTarget(imp)} data-testid={`import-rollback-${imp.id}`} title="Move all leads created by this import to the recycle bin" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50"><Undo2 className="h-3.5 w-3.5" />Rollback</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Import issues */}
      {issues.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-white p-5">
          <h2 className="mb-4 flex items-center gap-2 font-sora font-semibold text-navy"><FileWarning className="h-5 w-5 text-red-500" />Import Issues ({issues.length})</h2>
          <div className="space-y-2">
            {issues.slice(0, 30).map((iss) => (
              <div key={iss.id} className="flex items-start gap-3 rounded-lg bg-red-50/60 px-3 py-2 text-sm">
                <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Row {iss.row_number}</span>
                <div><p className="font-medium text-red-700">{iss.issue_type}</p><p className="text-xs text-red-600/80">{iss.issue_details}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ColumnMappingModal open={mapOpen} onOpenChange={setMapOpen} preview={preview} onConfirmed={load} />
      <CrmImportWizard open={crmOpen} onOpenChange={setCrmOpen} preview={preview} onConfirmed={load} />
      <ConfirmDialog
        open={!!rollbackTarget}
        onOpenChange={(v) => !v && setRollbackTarget(null)}
        title="Roll back this import?"
        description={rollbackTarget ? `All ${rollbackTarget.new_leads} lead(s) created by this import will be moved to the recycle bin. Re-synced/updated leads are not affected. You can restore them later.` : ""}
        confirmLabel="Roll back"
        testid="rollback-confirm"
        onConfirm={() => doRollback(rollbackTarget)}
      />
    </div>
  );
}
